import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import intl from 'react-intl-universal';
import { Button, Modal, Typography } from '@getsynapse/design-system';
import VendorForm from 'Organisms/NewVendorModal/VendorForm';
import { ProjectVendor } from 'utils/customTypes';
import {
  displayNotification,
  setNotificationText,
  setNotificationVariant,
  setNotificationTimeout,
} from 'state/InlineNotification/inlineNotificationSlice';
import { addVendor } from 'state/Vendors/vendorsSlice';
import UnsavedChangesModal from 'Organisms/UnsavedChangesModal/UnsavedChangesModal';

const ADD_VENDOR_FORM = 'add_vendor_form';

const NewVendorModal = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [hasName, setHasName] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const onClose = () => {
    setIsOpen(false);
    setIsLoading(false);
    setHasName(false);
    setHasChanges(false);
  };

  const onSubmit = useCallback<(vendor: Partial<ProjectVendor>) => void>(
    async (vendor) => {
      setIsLoading(true);
      await dispatch(addVendor(vendor));
      dispatch(
        setNotificationText(intl.get('VENDORS_PAGE.SUCCESS_NOTIFICATION'))
      );
      dispatch(setNotificationVariant('success'));
      dispatch(setNotificationTimeout(4000));
      dispatch(displayNotification());
      onClose();
    },
    [dispatch]
  );

  const handleNameChange = useCallback((value) => setHasName(value), []);

  useEffect(() => {
    if (hasName) {
      setHasChanges(true);
    } else {
      setHasChanges(false);
    }
  }, [hasName]);

  return (
    <>
      <Button onClick={() => setIsOpen(true)} data-cy='add-vendor-button'>
        {intl.get('VENDORS_PAGE.ADD_VENDOR_LINK')}
      </Button>

      <Modal
        isOpen={isOpen}
        closeModal={() => {
          if (hasChanges) {
            setShowUnsavedChangesModal(true);
          } else {
            setShowUnsavedChangesModal(false);
            onClose();
          }
        }}
        aria-label='Add a Vendor'
        data-cy='add-vendor-modal'
        title={intl.get('VENDORS_PAGE.ADD_VENDOR_LINK')}
        size='large'
        className='h-screen'
        childrenClassName='max-h-136'
        actionButtons={[
          {
            children: intl.get('TEAMS.SAVE'),
            variant: 'primary',
            'data-cy': 'confirm-button',
            form: ADD_VENDOR_FORM,
            disabled: !hasName,
            loading: isLoading,
          },
          {
            children: intl.get('CANCEL'),
            variant: 'tertiary',
            onClick: onClose,
            'data-cy': 'cancel-button',
          },
        ]}
      >
        <Typography variant='body' className='mb-6'>
          {intl.get('VENDORS_PAGE.VENDOR_DETAILS')}
        </Typography>

        <VendorForm
          formId={ADD_VENDOR_FORM}
          onSubmit={onSubmit}
          onNameChange={handleNameChange}
          onDataChange={(changes) => setHasChanges(changes)}
        />
        <UnsavedChangesModal
          isOpen={showUnsavedChangesModal}
          setIsOpen={setShowUnsavedChangesModal}
          onConfirm={() => onClose()}
        />
      </Modal>
    </>
  );
};

export default NewVendorModal;
